import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuditFindingView, AuditSeverity, AuditView } from '../../types/agent';
import { CellText } from './CellLink';

/** The structural audit of a turn (sculpt-api PLAN-pf-skills.md §5): the counts, filters, and every finding laid
 *  out flat — nothing to open: severity and category, title, first location as a link ("+N" selects them all),
 *  what it reaches, the evidence, the fix and its Fix button — then the button that writes the same findings to an
 *  Audit Findings sheet. Drawn from the `structural_audit` tool's card — the model writes only a
 *  short summary above it — so the list is complete and costs no tokens. A finding the model dismissed after
 *  checking is not in the card; one it fixed stays, marked fixed with what was changed, and offers no Fix. The
 *  places its words name — in the title, the evidence, a note, the fix — are links too (`CellText`). */
type Filter = AuditSeverity | 'hidden';
type SheetState = 'idle' | 'writing' | 'done' | 'error';

const BADGE: Record<AuditSeverity, string> = {
  Critical: 'bg-danger-bg text-danger',
  Warning: 'bg-amber-50 text-amber-800',
  Info: 'bg-hover text-muted',
};

export function AuditCard({ view, onShowCell, sheets, onWriteSheet, onFix, sheetUpdated }: {
  view: AuditView;
  onShowCell?: (sheet: string, cell: string) => void;
  /** The workbook's sheet names: a place in a finding's words is a link only when it names one of them. */
  sheets?: readonly string[];
  /** Writes the Audit Findings sheet; absent in a read-only thread. */
  onWriteSheet?: (view: AuditView) => Promise<unknown>;
  /** Asks the agent to apply a finding's fix (a chat turn, with its approvals and undo); absent in a read-only
   *  thread, and while a turn is running. */
  onFix?: (finding: AuditFindingView) => void;
  /** The workbook's Audit Findings sheet was rewritten from this card when its turn ended. */
  sheetUpdated?: boolean;
}) {
  const { t } = useTranslation();
  const fixedCount = view.findings.filter((f) => f.fixed).length;
  const first: Filter = view.counts.Critical ? 'Critical' : view.counts.Warning ? 'Warning' : 'Info';
  const [filter, setFilter] = useState<Filter>(first);
  const [sheet, setSheet] = useState<SheetState>('idle');
  const hiddenCount = view.findings.filter((f) => f.hidden && f.severity !== 'Info').length;
  const shown = useMemo(
    () => view.findings.filter((f) => (filter === 'hidden' ? f.hidden && f.severity !== 'Info' : f.severity === filter && (!f.hidden || f.severity === 'Info'))),
    [view, filter],
  );
  const write = async () => {
    if (!onWriteSheet) return;
    setSheet('writing');
    try {
      await onWriteSheet(view);
      setSheet('done');
    } catch {
      setSheet('error');
    }
  };
  const tabs: { key: Filter; label: string; count: number }[] = [
    { key: 'Critical', label: 'Critical', count: view.counts.Critical },
    { key: 'Warning', label: 'Warning', count: view.counts.Warning },
    { key: 'Info', label: 'Info', count: view.counts.Info },
    ...(hiddenCount ? [{ key: 'hidden' as Filter, label: t('audit.hidden'), count: hiddenCount }] : []),
  ];
  return (
    <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="audit-card">
      <div className="border-b border-line-row px-3 py-2">
        <div className="text-[12.5px] font-medium text-ink">{t('audit.title')}</div>
        <div className="mt-0.5 text-[11.5px] text-muted">
          {view.outputs.length
            ? <CellText text={t('audit.outputs', { outputs: view.outputs.slice(0, 4).join(', ') })} sheets={sheets} onSelectRange={onShowCell} />
            : t('audit.noOutputs')}
          {view.iterative_calculation === false && <span className="text-danger"> · {t('audit.iterationOff')}</span>}
          {fixedCount > 0 && <span className="text-accent" data-testid="audit-fixed-count"> · {t('audit.fixedCount', { count: fixedCount })}</span>}
        </div>
      </div>
      <div className="flex gap-1 border-b border-line-row px-2 py-1.5" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={filter === tab.key}
            data-testid={`audit-tab-${tab.key}`}
            disabled={!tab.count}
            onClick={() => setFilter(tab.key)}
            className={`rounded-sm px-2 py-0.5 text-[11.5px] ${filter === tab.key ? 'bg-ink text-white' : 'text-muted hover:bg-hover'} disabled:opacity-40`}
          >
            {tab.label} {tab.count}
          </button>
        ))}
      </div>
      <div className="flex flex-col">
        {shown.length === 0 && <div className="px-3 py-2 text-[11.5px] text-muted">{t('audit.none')}</div>}
        {shown.map((f) => (
          <FindingRow key={f.id} f={f} onShowCell={onShowCell} sheets={sheets} onFix={onFix} />
        ))}
      </div>
      {onWriteSheet && view.findings.length > 0 && (
        <div className="border-t border-line-row px-3 py-2">
          {sheet === 'done' || (sheetUpdated && sheet === 'idle') ? (
            <span className="text-[11.5px] text-accent" data-testid="audit-sheet-done">✓ {t(sheet === 'done' ? 'audit.sheetDone' : 'audit.sheetUpdated')}</span>
          ) : (
            <button
              type="button"
              data-testid="audit-write-sheet"
              disabled={sheet === 'writing'}
              onClick={() => void write()}
              className="rounded-sm border border-line-btn px-2.5 py-1 text-[11.5px] text-ink hover:bg-hover disabled:opacity-50"
            >
              {sheet === 'writing' ? t('audit.sheetWriting') : t('audit.writeSheet')}
            </button>
          )}
          {sheet === 'error' && <span className="ml-2 text-[11.5px] text-danger">{t('audit.sheetFailed')}</span>}
        </div>
      )}
    </div>
  );
}

function Place({ sheet, address, onSelect }: { sheet: string; address: string; onSelect?: (sheet: string, address: string) => void }) {
  const label = `${sheet}!${address}`;
  if (!onSelect) return <span className="font-mono text-[11px] text-ink">{label}</span>;
  return (
    <button type="button" data-testid="audit-place" onClick={() => onSelect(sheet, address)} title={label}
      className="min-w-0 truncate font-mono text-[11px] text-accent underline-offset-2 hover:underline">
      {label}
    </button>
  );
}

function FindingRow({ f, onShowCell, sheets, onFix }: {
  f: AuditFindingView;
  onShowCell?: (sheet: string, cell: string) => void;
  sheets?: readonly string[];
  onFix?: (finding: AuditFindingView) => void;
}) {
  const { t } = useTranslation();
  const [fixAsked, setFixAsked] = useState(false);
  const firstPlace = f.locations[0];
  const more = f.locations.length - 1;
  // "+N" selects every location of the finding on the first one's sheet at once, rather than opening a list.
  const sameSheet = firstPlace ? f.locations.filter((l) => l.sheet === firstPlace.sheet).map((l) => l.address) : [];
  const others = f.locations.slice(1).map((l) => `${l.sheet}!${l.address}`).join(', ');
  const reaches = f.output ? t('audit.reaches', { output: f.output }) : f.impact ? null : t('audit.feedsNothing');
  const canFix = !!f.fix && !f.fixed && f.severity !== 'Info' && !!onFix;
  return (
    <div className="border-b border-line-row/60 px-3 py-2 last:border-b-0" data-testid="audit-finding">
      <div className="flex items-center gap-1.5 text-[10.5px]">
        <span className={`rounded-sm px-1.5 font-medium uppercase tracking-wide ${BADGE[f.severity]}`}>{f.severity}</span>
        <span className="text-fainter">{t(f.category === 'Error' ? 'audit.error' : 'audit.construction')}</span>
        {f.fixed && <span className="rounded-sm bg-accent-bg px-1.5 font-medium uppercase tracking-wide text-accent" data-testid="audit-fixed">✓ {t('audit.fixed')}</span>}
      </div>
      <div className="mt-0.5 text-[12px] text-ink"><CellText text={f.title} sheets={sheets} onSelectRange={onShowCell} /></div>
      <div className="mt-0.5 flex min-w-0 items-baseline gap-1.5 text-[11px] text-muted">
        {firstPlace && <Place sheet={firstPlace.sheet} address={firstPlace.address} onSelect={onShowCell} />}
        {more > 0 && (onShowCell ? (
          <button type="button" data-testid="audit-more" title={others}
            onClick={() => onShowCell(firstPlace.sheet, sameSheet.join(','))}
            className="shrink-0 text-accent hover:underline">+{more}</button>
        ) : <span className="shrink-0" title={others}>+{more}</span>)}
        {reaches && <span className="min-w-0 truncate">→ {reaches}</span>}
      </div>
      <div className="mt-0.5 truncate font-mono text-[10.5px] text-muted" title={f.evidence} data-testid="audit-evidence"><CellText text={f.evidence} sheets={sheets} onSelectRange={onShowCell} /></div>
      {f.notes.map((n) => (
        <div key={n} className="mt-0.5 text-[11px] text-muted"><CellText text={n} sheets={sheets} onSelectRange={onShowCell} /></div>
      ))}
      {f.fixed ? (
        <div className="mt-1 text-[11px] text-accent" data-testid="audit-fixed-what"><CellText text={f.fixed} sheets={sheets} onSelectRange={onShowCell} /></div>
      ) : (f.fix || canFix) && (
        <div className="mt-1 flex items-start gap-2">
          {f.fix && <div className="min-w-0 flex-1 text-[11px] text-ink"><CellText text={f.fix} sheets={sheets} onSelectRange={onShowCell} /></div>}
          {canFix && (
            <button type="button" data-testid="audit-fix" disabled={fixAsked}
              onClick={() => { setFixAsked(true); onFix?.(f); }}
              className="shrink-0 rounded-sm border border-line-btn px-2 py-0.5 text-[11px] text-ink hover:bg-hover disabled:opacity-50">
              {fixAsked ? t('audit.fixRequested') : t('audit.fix')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
